import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 */
export default class FromPageTrackingJsonToTrackingDataValueObjectMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Object} param.trackingDataObjectMapper
   * @param {Function} param.toQueryString
   * @param {Function} param.fromCamelToSnakeCase
   * @param {Function} param.pipe
   * @param {Object} param.valueObject
   */
  constructor({config, trackingDataObjectMapper, toQueryString, fromCamelToSnakeCase, pipe, valueObject}) {
    super()

    this._config = config
    this._trackingDataObjectMapper = trackingDataObjectMapper
    this._toQueryString = toQueryString
    this._fromCamelToSnakeCase = fromCamelToSnakeCase
    this._pipe = pipe
    this._valueObject = valueObject
  }

  setParams = ({page, culture}) => {
    const {TRACKING_TRANSLATIONS_BY_CULTURE} = this._config.get('tracking')
    const translations = TRACKING_TRANSLATIONS_BY_CULTURE && TRACKING_TRANSLATIONS_BY_CULTURE[culture]
    this._page = page
    this._pageName = this._page && translations && translations[`${this._page}_PAGE_NAME`]

    return this
  }

  /**
   * @param {Object} params
   * @param {String} params.event
   * @param {Boolean} params.reset
   */
  map = ({event, ...properties} = {}) => {
    const baseData = this._getBaseData({
      page: this._page
    })
    const {event: baseEvent, ...baseProperties} = baseData
    const data = this._getData({baseProperties, properties})
    const filteredData = this._removeUndefinedProperties(data)
    const trackingData = {
      event: event || baseEvent // We need the base event if any custom isn't passed.
    }
    // Only set data attribute if the object is not empty.
    if (Object.keys(filteredData).length > 0) trackingData.data = filteredData

    // Add site data only in events with data
    return this._valueObject(trackingData)
  }

  /**
   * @method
   * @private
   */
  _getData = ({baseProperties, properties}) => {
    const {filters, filters_string: filtersString, ...rest} = properties
    const splitFilters = typeof filters !== 'undefined' && this._trackingDataObjectMapper.map(filters)

    const data = {
      ...baseProperties,
      page_name: this._pageName,
      ...splitFilters,
      filters,
      ...rest // It could override `page_name` for special conditions.
    }

    if (data.searchParams) {
      data.hit_information = this._getHitInformationFromSearchParams(data.searchParams)
      delete data.searchParams
    }

    if (data.filters) data.filters = this._pipe(this._mapFilters, this._parseQueryParams)(data.filters)

    if (!data.filters) data.filters = filtersString

    if (data.last_filter) data.last_filter = this._pipe(this._mapFilters, this._parseQueryParams)(data.last_filter)

    return data
  }

  // setting attribution data from utm tags
  _getHitInformationFromSearchParams = searchParams => {
    const {UTM_TAGS} = this._config.get('COMMON').QUERY_PARAMS
    const UTM_TAGS_VALUES = this._config.get('COMMON').UTM_TAGS_VALUES

    if (searchParams[UTM_TAGS.SOURCE] && searchParams[UTM_TAGS.SOURCE] === UTM_TAGS_VALUES[UTM_TAGS.SOURCE].EMAIL)
      return 'from_email'
  }

  /**
   * @method
   * @private
   */
  _removeUndefinedProperties = obj => {
    const keys = Object.keys(obj)

    return keys.reduce((acc, key) => {
      if (obj[key] !== undefined) acc[key] = obj[key]
      return acc
    }, {})
  }

  /**
   * @method
   * @private
   */
  _mapFilters = filters =>
    Object.keys(filters)
      .sort()
      .reduce(
        (newFilters, filter) => ({
          ...newFilters,
          [this._fromCamelToSnakeCase(filter)]: filters[filter]
        }),
        {}
      )

  /**
   * @method
   * @private
   */
  _parseQueryParams = queryParams =>
    decodeURIComponent(this._toQueryString(queryParams, {arrayFormat: 'repeat', delimiter: ':'})).toLowerCase()

  /**
   * @method
   * @private
   */
  _getBaseData({page}) {
    const {TRACKING_DATA_BY_SITE, TRACKING_DATA_BY_PAGE} = this._config.get('tracking')
    const trackingDataByPage = TRACKING_DATA_BY_PAGE[page]
    const hasEvent = trackingDataByPage?.event
    const hasOnlyEventProperty = hasEvent && Object.keys(trackingDataByPage).length === 1

    // Add site data only in events with data
    return hasOnlyEventProperty ? {...trackingDataByPage} : {...TRACKING_DATA_BY_SITE, ...trackingDataByPage}
  }
}
